/**
 * <sunday-m-feel>: "Press to feel it." on a mattress page (sections/sunday-m-feel.liquid), ported from the
 * mockup (v9 press and drop, v70 tabs). Facts come from the records in the child JSON: each mattress's
 * layers (inches), press { depth, recoverMs }, drop { sinkCm, damping, recoverTau }, and its words.
 * Drawing colours are the theme's drawing tokens. Clips: muted, looping, poster first, playing only while
 * at least 40% on screen (on a phone only the one nearest the middle).
 * 15 Oct (AR, phone): press and drop move only when the person's own finger starts them, so they always animate,
 * Reduce Motion or not. A clip the phone won't autoplay (Reduce Motion, Low Power Mode) shows its play button.
 */
import { esc, calm } from '@sunday/util';

const FILLS = ['var(--sd-art-latex)', 'var(--sd-art-latex2)', 'var(--sd-art-latex3)'];
const INK = 'var(--sd-ink2)';
const fill = (s, v) => String(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in v ? v[k] : m));
const lower1 = (s) => String(s || '').replace(/^\p{Lu}(?=\p{Ll})/u, (c) => c.toLowerCase());

/* press: the dent in drawing units (the mattress is 96 tall); record depth is in the same units */
function pressPaths(m, d) {
  const L = m.layers.length ? m.layers : [{ in: 1, name: '' }];
  const tot = L.reduce((a, l) => a + Number(l.in), 0);
  const sc = 96 / tot;
  const y0 = 40;
  const xs = [];
  for (let x = 0; x <= 360; x += 10) xs.push(x);
  const bnd = [0];
  let cum = 0;
  L.forEach((l) => bnd.push((cum += Number(l.in))));
  const curve = (j) => {
    const yb = y0 + bnd[j] * sc;
    const f = 1 - (bnd[j] / tot) * 0.9;
    return xs.map((x) => [x, yb + d * f * Math.exp(-Math.pow((x - 180) / (58 + bnd[j] * 6), 2))]);
  };
  const P = (a) => a.map((p) => p[0] + ' ' + p[1].toFixed(1)).join('L');
  let out = '';
  for (let j = 0; j < L.length; j++) {
    const a = curve(j);
    const b = curve(j + 1).reverse();
    out += `<path d="M${P(a)}L${P(b)}Z" fill="${L.length === 1 ? FILLS[0] : FILLS[j] || FILLS[2]}"/>`;
  }
  let c2 = 0;
  L.forEach((l) => {
    const h = Number(l.in) * sc;
    const y = y0 + c2 * sc + h / 2 + 4;
    c2 += Number(l.in);
    if (h >= 11) out += `<text x="12" y="${(y - (h < 14 ? 1.5 : 0)).toFixed(1)}" font-size="${h >= 22 ? 11 : h >= 14 ? 9.5 : 8.5}" fill="${INK}">${esc(String(Number(l.in)).replace(/\.0$/, ''))}″ ${esc(l.name)}</text>`;
  });
  out += `<path d="M${P(curve(0))}" fill="none" stroke="${m.colour}" stroke-width="4"/>`;
  return out;
}

/* drop: the mockup's spring model, tuned per mattress from its record */
function dropCfg(m) {
  const r = m.drop || {};
  const d = Number(r.sinkCm || 4) / 0.17; // drawing units: sinks about d*.17 cm (mockup scale)
  const z = Number(r.damping ?? 0.3);
  const tau = Number(r.recoverTau ?? 0.1);
  const kk = 1500 / d;
  return { d, z, tau, kk, cc: 2 * z * Math.sqrt(kk) };
}
function dropSVG(m, st) {
  const y0 = 40;
  const H = 92;
  const xs = [];
  for (let x = 0; x <= 360; x += 6) xs.push(x);
  const dent = (x) => st.s * Math.exp(-Math.pow((x - 150) / 46, 2));
  const sf = (x) => y0 + dent(x);
  const L = m.layers.length ? m.layers : [{ in: 1 }];
  const tot = L.reduce((a, l) => a + Number(l.in), 0);
  const sc = H / tot;
  const bnd = [0];
  let c = 0;
  L.forEach((l) => bnd.push((c += Number(l.in))));
  const lay = (dp) => xs.map((x) => [x, y0 + dp + dent(x) * Math.max(0, 1 - dp / (H * 1.05))]);
  const P2 = (a) => a.map((p) => p[0] + ' ' + p[1].toFixed(1)).join('L');
  let o = '';
  for (let j = 0; j < L.length; j++) {
    const a = lay(bnd[j] * sc);
    const b = lay(bnd[j + 1] * sc).reverse();
    o += `<path d="M${P2(a)}L${P2(b)}Z" fill="${L.length === 1 ? FILLS[0] : FILLS[j] || FILLS[2]}"/>`;
  }
  o += `<path d="M${P2(xs.map((x) => [x, sf(x)]))}" fill="none" stroke="${m.colour}" stroke-width="3.5"/><path d="M0 ${y0 + H + 1}h360" stroke="var(--sd-art-khaki)" stroke-width="2"/>`;
  const wy = st.y < 0 ? y0 + st.y : sf(150);
  o += `<g transform="translate(150 ${wy.toFixed(1)})"><path d="M-13 -42a13 13 0 0 1 26 0" fill="none" stroke="var(--sd-art-shadow)" stroke-width="7"/><path d="M-22 -22a22 22 0 0 0 44 0v-6a22 22 0 0 0-44 0z" fill="var(--sd-art-shadow)"/><ellipse cx="0" cy="-2" rx="22" ry="4" fill="var(--sd-art-night)"/><text x="0" y="-19" text-anchor="middle" font-size="11" font-weight="700" fill="var(--sd-art-cream)">5 kg</text></g>`;
  const gx = 292;
  const gy = sf(gx);
  const ang = (Math.atan((sf(gx + 4) - sf(gx - 4)) / 8) * 180) / Math.PI;
  o += `<g transform="translate(${gx} ${gy.toFixed(1)}) rotate(${ang.toFixed(1)})"><path d="M-11 0l-3 -36h28l-3 36z" fill="color-mix(in srgb, var(--sd-art-glass) 55%, transparent)" stroke="var(--sd-art-glass-edge)" stroke-width="1.6"/><path d="M-12.5 -26L12.5 -26L11 -1h-22z" fill="color-mix(in srgb, var(--sd-art-water) 55%, transparent)"/></g>`;
  return o;
}

class SundayMFeel extends HTMLElement {
  connectedCallback() {
    if (this.cfg) return;
    this.cfg = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this.t = this.cfg.text;
    this.mats = this.cfg.mattresses;
    this.pm = this.dataset.current;
    this.cur = this.mats.find((m) => m.id === this.dataset.current) || this.mats[0];
    this.press = { d: 0, raf: 0, on: false };
    this.$box = this.querySelector('[data-press]');
    this.$pl = this.querySelector('[data-pl]');
    this.$drop = this.querySelector('[data-drop]');
    this.$dg = this.querySelector('[data-dg]');
    this.$out = this.querySelector('[data-dout]');

    const pills = this.querySelector('[data-pills]');
    if (pills) pills.innerHTML = this.mats
      .map((m) => `<button type="button" data-pm="${esc(m.id)}" aria-pressed="${m.id === this.pm}"><i style="background:${esc(m.colour)}"></i>${esc(m.name)}</button>`)
      .join('');
    this.#paintPress();
    this.#dropInit();

    this.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-tab]');
      if (tab) return this.#tab(tab.dataset.tab);
      const pm = e.target.closest('[data-pm]');
      if (pm) {
        this.pm = pm.dataset.pm;
        this.querySelectorAll('[data-pm]').forEach((b) => b.setAttribute('aria-pressed', String(b === pm)));
        this.press.d = 0;
        this.#paintPress();
      }
    });
    const down = (box, fn) => {
      box.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        try {
          box.setPointerCapture(e.pointerId);
        } catch (err) {}
        fn(true);
      });
      ['pointerup', 'pointercancel'].forEach((t) => box.addEventListener(t, () => fn(false)));
      box.addEventListener('keydown', (e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
          e.preventDefault();
          fn(true);
        }
      });
      box.addEventListener('keyup', (e) => {
        if (e.key === ' ' || e.key === 'Enter') fn(false);
      });
    };
    down(this.$box, (on) => (on ? this.#pressOn() : this.#pressOff()));
    down(this.$drop, (on) => this.#dropGo(on ? 'lift' : 'fall'));
    this.#clips();
  }

  #tab(v) {
    this.querySelectorAll('[data-tab]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.tab === v)));
    this.querySelectorAll('[data-p]').forEach((p) => (p.hidden = p.dataset.p !== v));
    this.dataset.t = v;
    this.#clipCheck();
  }

  /* ---- press ---- */
  get #pm() {
    return this.mats.find((m) => m.id === this.pm) || this.cur;
  }
  #depth() {
    return Number((this.#pm.press || {}).depth || 17);
  }
  #paintPress() {
    const m = this.#pm;
    this.$pl.innerHTML = pressPaths(m, this.press.d);
    this.$box.setAttribute('aria-label', fill(this.t.pressAria, { name: m.name }));
    this.querySelector('[data-head]').textContent = m.head || '';
    this.querySelector('[data-line]').textContent = `${this.t.comesBack} ${lower1(m.line)}`.trim();
  }
  #pressTo(target, dur) {
    cancelAnimationFrame(this.press.raf);
    const m = this.#pm;
    const d0 = this.press.d;
    const t0 = performance.now();
    if (dur <= 0) {
      this.press.d = target;
      this.$pl.innerHTML = pressPaths(m, target);
      return;
    }
    const step = (now) => {
      const k = Math.min(1, (now - t0) / dur);
      const ez = target > d0 ? 1 - Math.pow(1 - k, 3) : 1 - Math.pow(1 - k, 2.2);
      this.press.d = d0 + (target - d0) * ez;
      this.$pl.innerHTML = pressPaths(m, this.press.d);
      if (k < 1) this.press.raf = requestAnimationFrame(step);
    };
    this.press.raf = requestAnimationFrame(step);
  }
  #pressOn() {
    if (this.press.on) return;
    this.press.on = true;
    this.$box.classList.add('sd-on');
    this.$box.querySelector('.sd-mfl-fing').style.transform = `translateY(${this.#depth() + 4}px)`;
    this.#pressTo(this.#depth(), 240);
  }
  #pressOff() {
    if (!this.press.on) return;
    this.press.on = false;
    this.$box.classList.remove('sd-on');
    this.$box.querySelector('.sd-mfl-fing').style.transform = '';
    this.#pressTo(0, Number((this.#pm.press || {}).recoverMs || 260));
  }

  /* ---- drop ---- */
  #dropInit() {
    const c = dropCfg(this.cur);
    this.ds = { ...c, y: c.d, v: 0, s: c.d, t: 9, phase: 'rest', hit: true };
    this.$dg.innerHTML = dropSVG(this.cur, this.ds);
  }
  #say(s) {
    if (this.$out) this.$out.textContent = s;
  }
  #dropGo(phase) {
    const st = this.ds;
    const t = this.t;
    st.phase = phase;
    if (phase === 'lift') {
      st.tl = performance.now();
      this.#say(t.lifting);
    } else {
      st.v = 0;
      st.hit = false;
      st.t = 0;
      this.#say(t.falling);
    }
    this.$drop.classList.toggle('sd-held', phase === 'lift');
    cancelAnimationFrame(st.raf);
    const cm = (n) => (n * 0.17).toFixed(1);
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const n = 8;
      const h = dt / n;
      for (let i = 0; i < n; i++) {
        if (st.phase === 'lift') {
          st.y = Math.max(-70, st.y - 260 * h);
          st.s = Math.min(st.s * Math.exp(-h / st.tau), Math.max(0, st.y));
        } else {
          st.v += 1500 * h;
          if (st.y > 0) {
            st.v -= (st.kk * st.y + st.cc * st.v) * h;
            if (!st.hit) {
              st.hit = true;
              st.t = 0;
            }
          }
          st.y += st.v * h;
          if (st.y > 0) st.s = Math.max(st.y, st.s * Math.exp(-h / st.tau));
          else st.s *= Math.exp(-h / st.tau);
        }
        if (st.hit) st.t += h;
      }
      this.$dg.innerHTML = dropSVG(this.cur, st);
      if (st.phase === 'lift' && st.y <= -70 && st.s < 0.5) {
        this.#say(fill(t.back, { s: ((now - st.tl) / 1000).toFixed(1) }));
        return;
      }
      if (st.phase === 'fall' && st.hit && Math.abs(st.v) < 4 && st.t > 0.6) {
        st.phase = 'rest';
        this.#say(`${fill(t.sinks, { cm: cm(st.s) })} ${t.still}`);
        return;
      }
      st.raf = requestAnimationFrame(step);
    };
    st.raf = requestAnimationFrame(step);
  }

  /* ---- clips ---- */
  #clips() {
    this.vids = [...this.querySelectorAll('video[data-clip]')];
    if (!this.vids.length || !('IntersectionObserver' in window)) return;
    this.ratio = new Map();
    const io = new IntersectionObserver(
      (es) => {
        es.forEach((e) => this.ratio.set(e.target, e.intersectionRatio));
        this.#clipCheck();
      },
      { threshold: [0, 0.4, 0.6, 1] }
    );
    this.vids.forEach((v) => io.observe(v));
    addEventListener('scroll', () => this.#clipCheck(), { passive: true });
  }
  #clipCheck() {
    if (!this.vids || !this.ratio) return;
    const phone = matchMedia('(max-width: 719px)').matches;
    const vis = this.vids.filter((v) => (this.ratio.get(v) || 0) >= 0.4 && v.offsetParent);
    let play = vis;
    if (phone && vis.length > 1) {
      const mid = innerHeight / 2;
      const d = (v) => {
        const r = v.getBoundingClientRect();
        return Math.abs(r.top + r.height / 2 - mid);
      };
      play = [vis.sort((a, b) => d(a) - d(b))[0]];
    }
    this.vids.forEach((v) => {
      if (play.includes(v)) {
        if (v.preload === 'none') v.preload = 'metadata';
        if (calm()) v.controls = true;
        else v.play().catch(() => (v.controls = true));
      } else v.pause();
    });
  }
}

if (!customElements.get('sunday-m-feel')) customElements.define('sunday-m-feel', SundayMFeel);
